import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const labels = { rapid: "Rápido", blitz: "Blitz", bullet: "Bullet", classical: "Clássico", classic: "Clássico", correspondence: "Correspondência" };
const platforms = [["fsx", "FSX", ["classic", "rapid", "blitz"]], ["lichess", "Lichess", ["rapid", "blitz", "bullet", "classical", "correspondence"]], ["chess_com", "Chess.com", ["rapid", "blitz", "bullet"]]];
const normalize = value => String(value || "").trim().toLowerCase();

export function matchingRatings(payload, accounts) {
  return Object.fromEntries(platforms.map(([key]) => {
    const account = accounts[key];
    const item = payload?.[key];
    const matches = account && (key === "fsx" ? Number(item?.player_id) === Number(account) : normalize(item?.username) === normalize(account));
    return [key, matches ? item : {
      platform: key, username: key === "fsx" ? null : account,
      player_id: key === "fsx" ? account : null, ratings: {},
      status: account ? "pending" : "not_configured",
    }];
  }));
}

export default function OnlineRatings({ athleteId, chessComUsername, lichessUsername, fsxPlayerId, onData }) {
  const accountKey = JSON.stringify([athleteId, chessComUsername, lichessUsername, fsxPlayerId]);
  const callback = useRef(onData);
  const visible = useRef(null);
  callback.current = onData;
  const [loaded, setLoaded] = useState(null);
  const [updating, setUpdating] = useState(true);
  const [error, setError] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const accounts = { chess_com: chessComUsername, lichess: lichessUsername, fsx: fsxPlayerId };
  const data = loaded?.key === accountKey ? loaded.data : matchingRatings(null, accounts);

  useEffect(() => {
    if (!athleteId) return;
    let active = true;
    let timer;
    let retryCount = 0;
    const controller = new AbortController();
    const accounts = { chess_com: chessComUsername, lichess: lichessUsername, fsx: fsxPlayerId };
    let current = matchingRatings(visible.current?.key === accountKey ? visible.current.data : null, accounts);
    const publish = payload => {
      if (!active) return;
      current = matchingRatings(payload, accounts);
      visible.current = { key: accountKey, data: current };
      setLoaded({ key: accountKey, data: current });
      callback.current?.(current);
    };
    publish(current);
    setUpdating(true); setError(false); setCooldown(false);
    const refresh = async () => {
      if (!active) return;
      setUpdating(true); setError(false);
      let needsRetry = false;
      try {
        const response = await api.get(`/athletes/${athleteId}/online-ratings`, { signal: controller.signal, timeout: 45000 });
        if (!active) return;
        publish(response.data);
        needsRetry = Object.values(current).some(item => ["stale", "unavailable", "rate_limited", "pending"].includes(item.status));
      } catch {
        if (!active) return;
        setError(true);
        publish(Object.fromEntries(Object.entries(current).map(([key, item]) => [key,
          item.status === "not_configured" ? item : { ...item,
            status: ["ok", "saved", "stale"].includes(item.status) ? "stale" : "unavailable",
            refresh_status: "unavailable" },
        ])));
        needsRetry = true;
      } finally {
        if (active) setUpdating(false);
      }
      if (active && needsRetry) {
        setCooldown(true);
        timer = setTimeout(() => {
          if (!active) return;
          setCooldown(false);
          if (retryCount < 2) { retryCount += 1; refresh(); }
        }, 60000);
      }
    };
    const load = async () => {
      try {
        const response = await api.get(`/athletes/${athleteId}/online-ratings`, {
          params: { cache_only: true }, signal: controller.signal, timeout: 8000,
        });
        publish(response.data);
      } catch { /* A failed snapshot read still permits a fresh request. */ }
      if (active) refresh();
    };
    if (Object.values(accounts).some(Boolean)) load();
    else setUpdating(false);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [athleteId, chessComUsername, lichessUsername, fsxPlayerId, accountKey, attempt]);

  if (!athleteId) return <p className="text-xs text-slate-400">Carregando dados do atleta…</p>;
  if (!Object.values(accounts).some(Boolean)) return <p className="text-xs text-slate-400">Nenhum perfil Chess.com, Lichess ou FSX associado.</p>;
  return <div>
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4" aria-live="polite">
      <p className="text-xs text-slate-400">{updating ? "Atualizando ratings… Os valores salvos continuam visíveis." : error ? "Não foi possível atualizar. Os últimos dados disponíveis foram mantidos." : cooldown ? "Atualização temporariamente indisponível. Aguarde um minuto para uma nova tentativa." : "Consulte abaixo a última atualização de cada plataforma."}</p>
      <button type="button" disabled={updating || cooldown} onClick={() => setAttempt(value => value + 1)} className="rounded-lg border border-amber-500/40 px-3 py-2 text-xs text-amber-300 disabled:opacity-50 disabled:cursor-not-allowed">{updating ? "Atualizando…" : "Atualizar ratings"}</button>
    </div>
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{platforms.map(([key, name, modes]) => {
      if (!accounts[key]) return null;
      const item = data[key];
      const player = item.player || {};
      const titles = (Array.isArray(player.titles) ? player.titles : []).map(title => title.shortName || title.name).filter(Boolean);
      const validResponse = ["ok", "saved", "stale"].includes(item.status);
      const updated = item.last_success_at && new Date(item.last_success_at);
      return <div key={key} className="rounded-lg border border-amber-500/20 bg-[#0D2A52]/45 p-3">
        <div className="flex justify-between gap-2"><span className="text-sm text-white font-semibold">{name}</span><span className="text-xs text-slate-400 break-all">{item.username || (key === "fsx" ? `ID ${accounts[key]}` : accounts[key])}</span></div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">{modes.map(mode => {
          const detail = item.rating_details?.[mode];
          const value = detail?.games === 0 ? null : item.ratings?.[mode];
          return <div key={mode}>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{labels[mode]}</div>
            <div className={value == null ? "text-sm text-slate-400 mt-1" : "font-mono text-lg text-amber-300"}>{value ?? (item.status === "pending" ? "Consultando…" : validResponse ? (detail?.games === 0 ? "Sem partidas nesta modalidade" : "Sem rating informado") : "Consulta indisponível")}</div>
            {value != null && detail?.provisional && <p className="text-xs text-amber-200/80">Provisório</p>}
            {detail?.games > 0 && <p className="text-xs text-slate-400">{detail.games} partidas</p>}
          </div>;
        })}</div>
        {player.club?.name && <p className="text-xs text-slate-400 mt-3">Clube: {player.club.name}</p>}
        {titles.length > 0 && <p className="text-xs text-slate-400 mt-1">Títulos: {titles.join(", ")}</p>}
        {key === "fsx" && <div className="mt-3 border-t border-white/10 pt-2 text-xs text-slate-400">Fonte: Federação Sergipana de Xadrez · <a className="hover:text-amber-300" href={`https://www.fsx.org.br/jogadores/${item.player_id}`} target="_blank" rel="noreferrer">Ver perfil</a></div>}
        {item.puzzle_record?.value != null && <div className="mt-3 border-t border-white/10 pt-2 text-xs text-slate-300">Recorde de puzzles ({item.puzzle_record.label}): <span className="font-semibold text-amber-300">{item.puzzle_record.value}</span></div>}
        {item.status === "stale" && <p className="text-xs text-amber-200 mt-3">Exibindo o último dado salvo; a atualização está temporariamente indisponível.</p>}
        {item.status === "saved" && <p className="text-xs text-slate-400 mt-3">Dados salvos. Aguardando atualização.</p>}
        {item.status === "unavailable" && <p className="text-xs text-amber-200 mt-3">Não foi possível consultar esta plataforma. Isso não significa que o atleta esteja sem rating.</p>}
        {(item.status === "rate_limited" || item.refresh_status === "rate_limited") && <p className="text-xs text-amber-200 mt-2">Limite temporário da plataforma. A próxima consulta respeitará o intervalo de espera.</p>}
        {item.status === "not_found" && <p className="text-xs text-slate-400 mt-2">Perfil não encontrado na plataforma.</p>}
        {item.status === "disabled" && <p className="text-xs text-slate-400 mt-2">Esta conta está desativada no Lichess.</p>}
        {updated && !Number.isNaN(updated.getTime()) && <p className="text-xs text-slate-400 mt-3">Dados consultados em {updated.toLocaleString("pt-BR")}</p>}
      </div>;
    })}</div>
  </div>;
}
